import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParticipants, useTracks, isTrackReference, useConnectionState } from '@livekit/components-react';
import { ConnectionState, Track } from 'livekit-client';
import { ChevronUp, ChevronDown, Loader2, WifiOff, Users } from 'lucide-react';
import { useAppDispatch } from '../../../store/hooks';
import { participantJoinSession, participantLeaveSession } from '../../../store/slices/teamFocusSession';
import ParticipantCard from './ParticipantCard';
import MeetingControls from './MeetingControls';
import MobileParticipantsSidebar from './MobileParticipantsSidebar';
import MobileGrid from './MobileGrid';

interface VideoGridProps {
    teamId: string;
    isHost: boolean;
    isMuted: boolean;
    isVideoOn: boolean;
    isScreenSharing: boolean;
    isVideoDisabled?: boolean;
    onToggleMute: () => void;
    onToggleVideo: () => void;
    onToggleScreenShare: () => void;
    onEndCall: () => void;
    onEndSession: () => void;
    onToggleSidebar?: () => void;
}

const VideoGrid: React.FC<VideoGridProps> = ({
    teamId,
    isHost,
    isMuted,
    isVideoOn,
    isScreenSharing,
    isVideoDisabled = false,
    onToggleMute,
    onToggleVideo,
    onToggleScreenShare,
    onEndCall,
    onEndSession,
    onToggleSidebar,
}) => {
    const dispatch = useAppDispatch();
    const connectionState = useConnectionState();
    const participants = useParticipants();
    const [pinnedParticipantId, setPinnedParticipantId] = useState<string | null>(null);
    const [showAllParticipants, setShowAllParticipants] = useState(false);
    const [showEndSessionModal, setShowEndSessionModal] = useState(false);
    const [showMobileParticipantsSidebar, setShowMobileParticipantsSidebar] = useState(false);

    const isConnected = connectionState === ConnectionState.Connected;
    const isConnecting = connectionState === ConnectionState.Connecting || connectionState === ConnectionState.Reconnecting;

    // Call participant join API when connected
    useEffect(() => {
        if (isConnected && teamId) {
            dispatch(participantJoinSession(teamId));
        }

        // Cleanup: call leave when component unmounts
        return () => {
            if (teamId) {
                dispatch(participantLeaveSession(teamId));
            }
        };
    }, [dispatch, teamId, isConnected]);

    // Get all camera and screen share tracks at the grid level
    const allCameraTrackRefs = useTracks([Track.Source.Camera]);
    const allScreenShareTrackRefs = useTracks([Track.Source.ScreenShare]);

    // Filter to only actual track references (not placeholders)
    const allCameraTracks = useMemo(
        () => allCameraTrackRefs.filter(isTrackReference),
        [allCameraTrackRefs]
    );
    const allScreenShareTracks = useMemo(
        () => allScreenShareTrackRefs.filter(isTrackReference),
        [allScreenShareTrackRefs]
    );

    // Check if anyone is screen sharing
    const someoneIsSharing = participants.some(p => p.isScreenShareEnabled);

    // Determine pinned participant
    const pinnedParticipant = useMemo(() => {
        if (!pinnedParticipantId) return null;
        return participants.find(p => p.identity === pinnedParticipantId) || null;
    }, [pinnedParticipantId, participants]);

    // Get unpinned participants
    const unpinnedParticipants = useMemo(() => {
        return participants.filter(p => p.identity !== pinnedParticipantId);
    }, [participants, pinnedParticipantId]);

    const handlePin = useCallback((participantId: string) => {
        setPinnedParticipantId(participantId);
    }, []);

    const handleUnpin = useCallback(() => {
        setPinnedParticipantId(null);
    }, []);

    const toggleShowAll = useCallback(() => {
        setShowAllParticipants(prev => !prev);
    }, []);

    const toggleMobileParticipantsSidebar = useCallback(() => {
        setShowMobileParticipantsSidebar(prev => !prev);
    }, []);

    const closeMobileParticipantsSidebar = useCallback(() => {
        setShowMobileParticipantsSidebar(false);
    }, []);

    const handleLeaveCall = useCallback(() => {
        // Call leave API before leaving
        if (teamId) {
            dispatch(participantLeaveSession(teamId));
        }
        onEndCall();
    }, [dispatch, teamId, onEndCall]);

    const handleEndCall = useCallback(() => {
        if (isHost) {
            setShowEndSessionModal(true);
        } else {
            handleLeaveCall();
        }
    }, [isHost, handleLeaveCall]);

    const handleConfirmEndSession = useCallback(() => {
        setShowEndSessionModal(false);
        // Call leave API before ending session
        if (teamId) {
            dispatch(participantLeaveSession(teamId));
        }
        onEndSession();
    }, [dispatch, teamId, onEndSession]);

    const handleCancelEndSession = useCallback(() => {
        setShowEndSessionModal(false);
    }, []);

    // Helper to find tracks for a participant
    const getParticipantTracks = useCallback((participantIdentity: string) => {
        const videoTrack = allCameraTracks.find(t => t.participant.identity === participantIdentity);
        const screenShareTrack = allScreenShareTracks.find(t => t.participant.identity === participantIdentity);
        return { videoTrack, screenShareTrack };
    }, [allCameraTracks, allScreenShareTracks]);

    // Get grid columns based on participant count
    const getGridColumns = (count: number) => {
        if (count <= 1) return 'grid-cols-1';
        if (count === 2) return 'grid-cols-2';
        if (count <= 4) return 'grid-cols-2';
        if (count <= 6) return 'grid-cols-3';
        return 'grid-cols-4';
    };

    // Get layout class based on participant count
    const getParticipantLayout = (count: number) => {
        // Mobile: always vertical stack, scrollable
        // Desktop: grid layout based on count
        const gridClass = getGridColumns(count);
        return `flex flex-col gap-2 overflow-y-auto sm:grid sm:overflow-visible ${gridClass} sm:gap-3`;
    };

    // Render a single participant card
    const renderParticipantCard = (participant: typeof participants[0]) => {
        const { videoTrack, screenShareTrack } = getParticipantTracks(participant.identity);
        return (
            <ParticipantCard
                key={participant.identity}
                participant={participant}
                isPinned={participant.identity === pinnedParticipantId}
                videoTrack={videoTrack}
                screenShareTrack={screenShareTrack}
                onPin={() => handlePin(participant.identity)}
                onUnpin={handleUnpin}
            />
        );
    };

    // Render participant grid
    const renderParticipantGrid = (
        participantsList: typeof participants,
    ) => {
        if (participantsList.length === 0) return null;

        const layoutClass = getParticipantLayout(participantsList.length);

        return (
            <div className={layoutClass}>
                {participantsList.map(renderParticipantCard)}
            </div>
        );
    };

    return (
        <div className="w-full h-full flex flex-col bg-gray-950 rounded-2xl overflow-hidden relative">
            {/* Main video area - full height */}
            <div className="flex-1 flex flex-col min-h-0 p-3 sm:p-4">
                {/* Pinned or main grid */}
                <div className="flex-1 flex flex-col min-h-0">
                    {isConnecting ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-white/60 gap-3">
                            <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                            <p className="text-sm font-medium">Connecting to room...</p>
                        </div>
                    ) : !isConnected && participants.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-white/60 gap-3">
                            <WifiOff className="w-8 h-8" />
                            <p className="text-sm font-medium">Waiting for connection...</p>
                        </div>
                    ) : pinnedParticipant ? (
                        <div className="flex-1 flex flex-col min-h-0 relative">
                            {/* Main pinned video - full width on mobile, row on desktop */}
                            <div className={`flex flex-col h-full gap-3 ${unpinnedParticipants.length > 0 ? 'sm:flex-row' : ''}`}>
                                {/* Main pinned video */}
                                <div className={`min-w-0 ${unpinnedParticipants.length > 0 ? 'sm:flex-1' : 'flex-1'}`}>
                                    {renderParticipantCard(pinnedParticipant)}
                                </div>
                                {/* Mobile: 2-column grid for remaining participants */}
                                {unpinnedParticipants.length > 0 && (
                                    <div className="sm:hidden flex-shrink-0 grid grid-cols-2 gap-2">
                                        {unpinnedParticipants.slice(0, 2).map(p => (
                                            <div key={p.identity} className="aspect-video w-full">
                                                {renderParticipantCard(p)}
                                            </div>
                                        ))}
                                        {unpinnedParticipants.length > 2 && (
                                            <div
                                                className="aspect-video bg-gray-900/80 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-900 transition-colors"
                                                onClick={toggleMobileParticipantsSidebar}
                                            >
                                                <Users className="w-6 h-6 text-white/60 mb-1" />
                                                <span className="text-white/80 text-xs font-medium">+{unpinnedParticipants.length - 2} more</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Sidebar - desktop only */}
                                {unpinnedParticipants.length > 0 && (
                                    <div
                                        className="hidden sm:flex flex-shrink-0 flex-col overflow-y-auto space-y-2 pr-1 transition-all duration-300"
                                        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent', width: '20rem' }}
                                    >
                                        {unpinnedParticipants.map(p => (
                                            <div key={p.identity} className="aspect-video w-full">
                                                {renderParticipantCard(p)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 overflow-hidden relative">
                            {/* Mobile layout */}
                            <div className="sm:hidden h-full">
                                <MobileGrid
                                    participants={participants}
                                    renderParticipantCard={renderParticipantCard}
                                    onToggleParticipantsSidebar={toggleMobileParticipantsSidebar}
                                />
                            </div>
                            {/* Desktop grid */}
                            <div className="hidden sm:block h-full overflow-y-auto">
                                {renderParticipantGrid(participants)}
                            </div>
                        </div>
                    )}

                    {/* Mobile participants sidebar overlay - works for both pinned and non-pinned */}
                    {showMobileParticipantsSidebar && (
                        <MobileParticipantsSidebar
                            participants={participants}
                            renderParticipantCard={renderParticipantCard}
                            onClose={closeMobileParticipantsSidebar}
                        />
                    )}
                </div>

                {/* Screen share expand/collapse toggle */}
                {someoneIsSharing && (
                    <div className="flex justify-center mt-2">
                        <button
                            onClick={toggleShowAll}
                            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm font-medium backdrop-blur-sm"
                        >
                            {showAllParticipants ? (
                                <>
                                    <ChevronDown className="w-4 h-4" />
                                    Show less
                                </>
                            ) : (
                                <>
                                    <ChevronUp className="w-4 h-4" />
                                    Show all participants ({participants.length})
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Meeting Controls - Floating overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-6 px-4 pointer-events-none">
                <div className="pointer-events-auto">
                    <MeetingControls
                        isMuted={isMuted}
                        isVideoOn={isVideoOn}
                        isScreenSharing={isScreenSharing}
                        isVideoDisabled={isVideoDisabled}
                        onToggleMute={onToggleMute}
                        onToggleVideo={onToggleVideo}
                        onToggleScreenShare={onToggleScreenShare}
                        onEndCall={handleEndCall}
                        onToggleSidebar={onToggleSidebar}
                        showParticipantsToggle={participants.length > 2}
                        onToggleParticipantsSidebar={toggleMobileParticipantsSidebar}
                        isParticipantsSidebarOpen={showMobileParticipantsSidebar}
                    />
                </div>
            </div>

            {/* End Session Confirmation Modal for Host */}
            {showEndSessionModal && (
                <div className="absolute inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop with blur */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={handleCancelEndSession}
                    />

                    {/* Modal */}
                    <div className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
                        {/* Icon and Content */}
                        <div className="flex flex-col items-center text-center">
                            {/* Icon */}
                            <div className="mb-4 bg-red-50 border-2 border-red-200 rounded-full p-4">
                                <svg
                                    className="w-8 h-8 text-red-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>

                            {/* Title */}
                            <h2 className="text-gray-900 text-2xl font-bold mb-2">
                                End Session?
                            </h2>

                            {/* Message */}
                            <p className="text-gray-600 text-sm leading-relaxed max-w-sm mb-6">
                                Are you sure you want to end this focus session? All participants will be disconnected.
                            </p>

                            {/* Buttons */}
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={handleCancelEndSession}
                                    className="flex-1 px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-200 active:scale-95"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmEndSession}
                                    className="flex-1 px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold transition-all duration-200 hover:shadow-lg active:scale-95"
                                >
                                    End Session
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoGrid;
